import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Tabs,
  Input,
  DatePicker,
  Table,
  Space,
  Pagination,
  Tag,
  Card,
  Typography,
  Button,
  Modal,
  message
} from 'antd';
import { FileExcelOutlined, CheckSquareOutlined, DeleteOutlined } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import TeacherRatingReport from './TeacherRatingReport';
import StudentClassesReminder from '../Student/StudentClassesReminder';
import TestScore from './TestScore';
import BookTest from './BookTest';
import StudentFeedbackReport from './StudentFeedbackReport';
import './Registrations.css';

const { Content } = Layout;
const { TabPane } = Tabs;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const RegistrationList = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(150);
  const [activeTab, setActiveTab] = useState('attendance');
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    }).toUpperCase();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: '2-digit'
    }).toUpperCase() + ' ' + 
    date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const fetchAttendanceData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
  
      const sortedAttendance = response.data.sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setAttendanceData(sortedAttendance);
      setFilteredClasses(sortedAttendance);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    }
  }, [API_URL]);
  

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

const handleDeleteAttendance = (attendanceId) => {
  Modal.confirm({
    title: 'Are you sure you want to delete this attendance record?',
    content: 'This action cannot be undone.',
    okText: 'Delete',
    okType: 'danger',
    cancelText: 'Cancel',
    centered: true,

    onOk: async () => {
      const token = localStorage.getItem('token');

      try {
        await axios.delete(`${API_URL}/attendance/${attendanceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        message.success('Attendance record deleted.');

        fetchAttendanceData(); // Refresh table
      } catch (error) {
        console.error('Error deleting attendance:', error);
        message.error('Failed to delete attendance.');
      }
    }
  });
};



  useEffect(() => {
    let filtered = attendanceData.filter((attendance) => {
      const studentName = (attendance.student_firstname || '') + ' ' + (attendance.student_nickname || '');
      const matchesStudent = studentName.toLowerCase().includes(studentSearchTerm.toLowerCase());
  
      const matchesCourse = attendance.class_code?.toLowerCase().includes(courseSearchTerm.toLowerCase());
  
      // Both search terms must match if both are provided
      return matchesStudent && matchesCourse;
    });
  
    if (fromDate && toDate) {
      filtered = filtered.filter((attendance) => {
        const attendanceDate = new Date(attendance.date);
        return attendanceDate >= fromDate && attendanceDate <= toDate;
      });
    }
  
    setFilteredClasses(filtered);
    setCurrentPage(1);
  }, [studentSearchTerm, courseSearchTerm, attendanceData, fromDate, toDate]);
  

  const currentItems = filteredClasses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const columns = [
    {
  title: t('student'),
  key: 'student',
  width: 200, // prevent automatic squeeze
  render: (text, record) => (
    <div
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.4',
      }}
    >
      {record.student_firstname || ''}
      {record.student_nickname && (
        <span style={{ marginLeft: 8, color: '#888' }}>
          ({record.student_nickname})
        </span>
      )}
    </div>
  )
},

    { title: t('course'), dataIndex: 'class_code', key: 'class_code' },
    { title: t('classid'), dataIndex: 'classid', key: 'classid' },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date),
    },
    { title: t('subject'), dataIndex: 'subject_name', key: 'subject_name' },
    { 
      title: t('teacher'), 
      dataIndex: 'teacher_nickname', 
      key: 'teacher_nickname',
      render: (teacher_nickname) => teacher_nickname || '-'
    },

    {
  title: t('Attendance & Homework'),
  key: 'attendance_homework',
  width: 160,
  render: (_, record) => {
    // Attendance tag
    let attColor = 'default';
    let attLabel = record.status;

    if (record.status === 'Present') {
      attColor = 'green';
      attLabel = t('present');
    } else if (record.status === 'Absent') {
      attColor = 'red';
      attLabel = t('absent');
    } else if (record.status === 'Late_15') {
      attColor = 'orange';
      attLabel = t('Late ≤ 15 M');
    } else if (record.status === 'Late_Over15') {
      attColor = 'volcano';
      attLabel = t('Late > 15 M');
    }

    // Homework tag
    let hwColor =
      record.homework === 'YES'
        ? 'green'
        : record.homework === 'NO'
        ? 'red'
        : 'default';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        Attendance<Tag color={attColor}>{attLabel}</Tag>
        Homework<Tag color={hwColor}>{record.homework || 'N/A'}</Tag>
      </div>
    );
  },
},

    { 
      title: t('comments'), 
      dataIndex: 'comment', 
      key: 'comment',
      width: 250,
      render: (text) => (
        <div 
          style={{ 
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            minWidth: '150px',
            maxWidth: '250px',
            lineHeight: '1.5'
          }}
        >
          {text || '-'}
        </div>
      )
    },
   {
  title: t('Timestamps'),
  key: 'timestamps',
  width: 180,
  render: (_, record) => (
    <div style={{ lineHeight: '1.4' }}>
      <div>
        <strong>{t('Created')}:</strong>
        <br />
        {formatDateTime(record.timestamp)}
      </div>
      <div style={{ marginTop: 6 }}>
        <strong>{t('Edited')}:</strong>
        <br />
        {record.edited_at ? formatDateTime(record.edited_at) : '-'}
      </div>
    </div>
  ),
},

{
  title: t('delete'),
  key: 'delete',
  width: 80,
  align: 'center',
  render: (_, record) => (
    <Button
      danger
      type="text"
      size="small"
      onClick={() => handleDeleteAttendance(record.attendance_id)}
    >
      <DeleteOutlined />
    </Button>
  ),
},


];

// Update the CSV data mapping
const csvData = currentItems.map((attendance) => ({
    student_nickname: attendance.student_nickname,
    student_firstname: attendance.student_firstname,
    course: attendance.class_code,
    classid: attendance.classid,
    subject: attendance.subject_name,
    teacher: attendance.teacher_nickname || '-',
    type: attendance.isprivate ? t('private') : t('group'),
    date: formatDate(attendance.date),
    attendance: attendance.status,
    homework: attendance.homework || 'N/A',
    comments: attendance.comment,
    timestamp: formatDateTime(attendance.timestamp),
    lastEdited: formatDateTime(attendance.edited_at)
}));

  return (
    <Layout>
      <Sidebar />
      <Layout>
        <Header />
        <Content style={{ marginTop: '60px', marginLeft: '70px', padding: '24px' }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab={t('Attendance')} key="attendance">
               <Card style={{ marginBottom: 16 }}>
                    <Title level={3}>
                      <CheckSquareOutlined  /> {t('Student Attendance')}</Title>
            <Space style={{ marginBottom: 16 }} wrap>
                <Input
                  placeholder={t('searchByStudentName')}
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  style={{ width: 200 }}
                  allowClear // Adds an X button to clear the input
                />
                <Input
                  placeholder={t('searchByCourse')}
                  value={courseSearchTerm}
                  onChange={(e) => setCourseSearchTerm(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <RangePicker
                  onChange={(dates) => {
                    setFromDate(dates ? dates[0].toDate() : null);
                    setToDate(dates ? dates[1].toDate() : null);
                  }}
                />
                <CSVLink 
                  data={csvData} 
                  filename="attendance_data.csv" 
                  className="ant-btn"
                >
                  <Button><FileExcelOutlined style={{ marginRight: 6 }} /> {t('Export CSV')}</Button>
                </CSVLink>
              </Space>

              <Table
                    columns={columns as any}
                    dataSource={currentItems}
                    pagination={false}
                    rowKey={(record) => record.attendance_id}
                    scroll={{ 
                      x: 1800, 
                    }}
                    style={{
                      overflowX: 'auto',
                      width: '100%'
                    }}
                />
                              <Pagination
                current={currentPage}
                total={filteredClasses.length}
                pageSize={itemsPerPage}
                onChange={setCurrentPage}
                showSizeChanger
                onShowSizeChange={(_, size) => setItemsPerPage(size)}
                style={{ marginTop: 16, marginBottom: 16 }}
              />
                 </Card>
            </TabPane>
            <TabPane tab={t('Student Feedback')} key="studentFeedback">
              <StudentFeedbackReport />
            </TabPane>
            <TabPane tab={t('Course End Reminder')} key="courseEndReminder">
              <StudentClassesReminder />
            </TabPane>
            <TabPane tab={t('Teacher Rating')} key="teacherRating">
              <TeacherRatingReport />
            </TabPane>
            <TabPane tab={t('Register for a Test')} key="bookTest">
              <BookTest />
            </TabPane>
            <TabPane tab={t('Student Test Scores')} key="testScore">
              <TestScore />
            </TabPane>
          </Tabs>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(RegistrationList, ['admin', 'superadmin']);
